/**
 * ResourceManagerDialog — inline CRUD for Rooms and Equipment.
 *
 * Opens from the Schedule view when the current resource tab is either
 * Room or Equipment. Keeps the admin flow in one place: they never have
 * to leave the calendar to add a new room / equipment record.
 *
 * Backend endpoints (Phase-2 workforce_schedule_routes):
 *   * GET/POST/PUT/DELETE `/api/workforce/rooms`
 *   * GET/POST/PUT/DELETE `/api/workforce/equipment`
 * Delete is soft (active=False) so historical calendar events still
 * resolve the resource name.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash, PencilSimple, Plus, X, House, Package } from "@phosphor-icons/react";

// Same soft palette that the Scheduler uses to colour swim-lanes. Kept
// small so users don't drown in a colour picker.
const COLOR_SWATCHES = [
  "#4A6753", "#6E8FA6", "#A78F5A", "#B7796B",
  "#7C6BB7", "#4B7A8A", "#8A6BB7", "#6B8A7A",
];

const EQUIPMENT_KINDS = [
  { value: "sensory",    label: "Sensory" },
  { value: "AAC",        label: "AAC (Communication)" },
  { value: "assessment", label: "Assessment" },
  { value: "mobility",   label: "Mobility" },
  { value: "other",      label: "Other" },
];

const empty = (resource) => ({
  name: "",
  branch_id: "",
  color: COLOR_SWATCHES[0],
  description: "",
  active: true,
  // resource-specific
  capacity: resource === "room" ? 1 : undefined,
  quantity: resource === "equipment" ? 1 : undefined,
  kind:     resource === "equipment" ? "other" : undefined,
});


export default function ResourceManagerDialog({ open, resource, onClose, onChanged, branches }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // row being edited (null = list mode)
  const [form, setForm] = useState(empty("room"));
  const [saving, setSaving] = useState(false);

  const endpoint = resource === "room" ? "/workforce/rooms" : "/workforce/equipment";
  const label = resource === "room" ? "Room" : "Equipment";
  const Icon = resource === "room" ? House : Package;

  const load = async () => {
    if (!resource) return;
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [open, resource]);

  const startAdd = () => {
    setForm(empty(resource));
    setEditing({ _new: true });
  };

  const startEdit = (row) => {
    setForm({
      name: row.name || "",
      branch_id: row.branch_id || "",
      color: row.color || COLOR_SWATCHES[0],
      description: row.description || "",
      active: row.active !== false,
      capacity: resource === "room"      ? (row.capacity  ?? 1) : undefined,
      quantity: resource === "equipment" ? (row.quantity  ?? 1) : undefined,
      kind:     resource === "equipment" ? (row.kind      ?? "other") : undefined,
    });
    setEditing(row);
  };

  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error(`${label} name is required`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        branch_id: form.branch_id || null,
        color: form.color,
        description: form.description || "",
        active: form.active,
      };
      if (resource === "room") payload.capacity = Number(form.capacity) || 1;
      if (resource === "equipment") {
        payload.quantity = Number(form.quantity) || 1;
        payload.kind = form.kind || "other";
      }
      if (editing?._new) {
        await api.post(endpoint, payload);
        toast.success(`${label} added`);
      } else {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success(`${label} updated`);
      }
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Deactivate ${label.toLowerCase()} "${row.name}"?\nExisting appointments referencing it will still show the name.`)) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast.success(`${label} deactivated`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setEditing(null); onClose?.(); } }}>
      <DialogContent className="sm:max-w-2xl bg-card" data-testid={`resource-manager-${resource || ""}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon size={18} weight="duotone" className="text-primary" />
            Manage {label.toLowerCase()}s
          </DialogTitle>
          <DialogDescription className="text-xs">
            Add, edit or deactivate {label.toLowerCase()}s used when booking appointments and displaying the workforce schedule.
          </DialogDescription>
        </DialogHeader>

        {!editing && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {rows.length} {label.toLowerCase()}{rows.length === 1 ? "" : "s"} total.
                Deactivated items stay in the database so historical calendar
                events keep their resource name.
              </p>
              <Button size="sm" onClick={startAdd} className="gap-1" data-testid="resource-add">
                <Plus size={13} /> Add {label.toLowerCase()}
              </Button>
            </div>

            <div className="border border-border rounded-md divide-y divide-border max-h-[420px] overflow-y-auto">
              {loading && <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>}
              {!loading && rows.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No {label.toLowerCase()}s yet.
                </div>
              )}
              {!loading && rows.map(row => {
                const branch = branches.find(b => b.id === row.branch_id);
                return (
                  <div
                    key={row.id}
                    className={`px-3 py-2.5 flex items-center gap-3 ${row.active === false ? "opacity-60" : ""}`}
                    data-testid={`resource-row-${row.id}`}
                  >
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: row.color || "#6B7280" }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-medium truncate">{row.name}</div>
                        {row.active === false && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                        {resource === "equipment" && row.kind && (
                          <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]">{row.kind}</Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {branch?.name || "All branches"}
                        {resource === "room" && row.capacity ? ` · capacity ${row.capacity}` : ""}
                        {resource === "equipment" && row.quantity ? ` · qty ${row.quantity}` : ""}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => startEdit(row)} className="gap-1 h-7 px-2" data-testid={`resource-edit-${row.id}`}>
                      <PencilSimple size={12} /> Edit
                    </Button>
                    {row.active !== false && (
                      <Button size="sm" variant="ghost" onClick={() => remove(row)} className="gap-1 h-7 px-2 text-red-700" data-testid={`resource-del-${row.id}`}>
                        <Trash size={12} />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {editing && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={resource === "room" ? "e.g. Sensory Room A" : "e.g. Peanut Ball"}
                  data-testid="resource-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Branch</Label>
                <Select value={form.branch_id || "__all__"} onValueChange={v => setForm(f => ({ ...f, branch_id: v === "__all__" ? "" : v }))}>
                  <SelectTrigger data-testid="resource-branch"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All branches</SelectItem>
                    {(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {resource === "room" && (
                <div className="space-y-1.5">
                  <Label>Capacity</Label>
                  <Input
                    type="number" min={1} max={999}
                    value={form.capacity}
                    onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))}
                    data-testid="resource-capacity"
                  />
                </div>
              )}

              {resource === "equipment" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Kind</Label>
                    <Select value={form.kind} onValueChange={v => setForm(f => ({ ...f, kind: v }))}>
                      <SelectTrigger data-testid="resource-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {EQUIPMENT_KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Quantity</Label>
                    <Input
                      type="number" min={1} max={999}
                      value={form.quantity}
                      onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
                      data-testid="resource-quantity"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Colour</Label>
              <div className="flex gap-2 flex-wrap">
                {COLOR_SWATCHES.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, color: c }))}
                    className={`w-7 h-7 rounded-full border-2 transition ${form.color === c ? "border-foreground scale-110" : "border-transparent hover:scale-105"}`}
                    style={{ background: c }}
                    aria-label={`Pick colour ${c}`}
                    data-testid={`resource-color-${c}`}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional short description"
                data-testid="resource-description"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {editing ? (
            <>
              <Button variant="outline" onClick={cancelEdit} className="gap-1" data-testid="resource-cancel">
                <X size={13} /> Cancel
              </Button>
              <Button onClick={save} disabled={saving} data-testid="resource-save">
                {saving ? "Saving…" : editing._new ? `Add ${label.toLowerCase()}` : "Save changes"}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={onClose} data-testid="resource-close">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
